import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { withFragments } from 'lib/gql';
import type { Font } from 'lib/text';
import { getTextSize } from 'lib/text';

import type { drawOwner_AnyCardInterface } from './__generated__/drawOwner.graphql';
import { getObtainedInfos } from './drawObtainedInfos';
import { getImage } from './getImage';

export const drawOwner = withFragments(
  async ({
    ctx,
    width,
    card,
    y,
    fontName,
    scale: scaleProp,
    fill = '#fff',
  }: {
    ctx: CanvasRenderingContext2D;
    width: number;
    card: drawOwner_AnyCardInterface;
    y: number;
    fontName: string;
    scale: number;
    fill?: string;
  }) => {
    if (card.tokenOwner?.user) {
      const { icon, label } = getObtainedInfos(card.tokenOwner.transferType);
      const obtainedIcon = await getImage(icon, fill === '#000');

      ctx.fillStyle = fill;
      ctx.textAlign = 'center';
      const fontTransfer: Font = `500 ${30 * scaleProp}px ${fontName}`;
      const { width: ownerWidth } = getTextSize(label, fontTransfer);

      ctx.save();
      ctx.globalCompositeOperation = 'overlay';
      ctx.globalAlpha = 0.5;
      const iconSize = 100 * scaleProp;
      ctx.drawImage(
        obtainedIcon,
        width / 2 - ownerWidth / 2 - iconSize / 2,
        y - iconSize / 2 - 10,
        iconSize,
        iconSize
      );
      ctx.restore();

      ctx.font = fontTransfer;
      ctx.fillText(label, width / 2, y);

      const fontUsername: Font = `600 ${60 * scaleProp}px ${fontName}`;
      ctx.font = fontUsername;
      const { width: nicknameWidth } = getTextSize(
        card.tokenOwner.user.nickname,
        fontUsername
      );
      const scale = Math.min((width * 0.8) / nicknameWidth, 1);
      ctx.save();
      ctx.translate(width / 2, y + 67 * scaleProp);
      ctx.scale(scale, scale);
      ctx.fillText(card.tokenOwner.user.nickname, 0, 0);
      ctx.restore();

      if (card.tokenOwner.from) {
        ctx.font = `${30 * scaleProp}px ${fontName}`;
        ctx.textAlign = 'center';
        ctx.fillText(
          new Date(card.tokenOwner.from)
            .toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
            .toLocaleUpperCase(),
          width / 2,
          y + 114 * scaleProp
        );
      }
    }
  },

  {
    AnyCardInterface: gql`
      fragment drawOwner_AnyCardInterface on AnyCardInterface {
        slug

        tokenOwner {
          id
          from
          transferType
          user {
            slug
            nickname
          }
        }
      }
    ` as TypedDocumentNode<drawOwner_AnyCardInterface>,
  }
);
